<template>
  <div v-show="shouldDisplayOverlaySelectMenuAndLabel">
    <label
      :for="`overlay-menu-${id}`"
      class="sc3-form-label sc3-form-label-inline sc3-form-label-small"
      v-if="shouldShowLabel"
    >{{ isIndicatorOverlayMenu ? 'OVERLAY' : 'OVERLAYS' }}</label>
    <br v-if="shouldShowLabel" />
    <select
      :class="{ 'hidden': !shouldDisplayOverlaySelectMenu }"
      class="sc3-select-menu sc3-select-menu-inline sc3-select-menu-compact sc3-select-menu-white-and-grey sc3-select-menu-no-margins"
      :id="`overlay-menu-${id}`"
      v-model="overlayName"
      @change="overlayMenuChanged($event)"
    >
      <option :value="NONE">{{ NONE }}</option>
      <option
        v-for="overlay in overlaysArray"
        :key="overlay.name"
        :value="overlay.name"
      >{{ overlay.name }}</option>
    </select>
  </div>
</template>

<script lang="ts" setup>
  import { ref, toRefs, computed, watch } from 'vue';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useIndicatorsAndOverlays } from '@/composables/useIndicatorsAndOverlays';
  import { useModals } from '@/composables/useModals';
  import { NONE, DATE_TIME_AXIS, STANDARD_OVERLAYS_ARRAY, OVERLAYS_OF_INDICATORS_ARRAY } from '@/enums/indicators-and-overlays';
  import { Overlay } from '@/interfaces/chart';

  const { isLoggedIn } = useAuthentication();
  const { MAXIMUM_NUMBER_OF_OVERLAYS_FOR_FREE_USERS } = useIndicatorsAndOverlays();
  const { launchCreateAccountModal } = useModals();
  const props = defineProps<{
    overlayName: string;
    isIndicatorOverlayMenu: boolean;
    id: number;
    shouldShowLabel: boolean;
    indicatorName: string;
    isAtLeastOneNonDateTimeAxisIndicatorPresent: boolean;
    overlays: Overlay[];
  }>();
  const {
    overlayName: parentOverlayName,
    isIndicatorOverlayMenu,
    id,
    shouldShowLabel,
    indicatorName,
    isAtLeastOneNonDateTimeAxisIndicatorPresent,
    overlays
  } = toRefs(props);

  const emit = defineEmits<{
    (e: 'update:overlayName', overlayName: string): void;
    (e: 'overlayMenuChanged', id: number, overlayName: string): void;
  }>();

  const overlayName = ref(parentOverlayName.value);

  const overlaysArray = computed(() => {
    return isIndicatorOverlayMenu.value ? OVERLAYS_OF_INDICATORS_ARRAY : STANDARD_OVERLAYS_ARRAY;
  });

  const shouldDisplayOverlaySelectMenuAndLabel = computed(() => {
    return !isIndicatorOverlayMenu.value || isAtLeastOneNonDateTimeAxisIndicatorPresent.value;
  });

  const shouldDisplayOverlaySelectMenu = computed(() => {
    if (!isIndicatorOverlayMenu.value) {
      return true;
    }

    return !!indicatorName.value && ![NONE, DATE_TIME_AXIS.name].includes(indicatorName.value);
  });

  function overlayMenuChanged(e: Event) {
    if (!(e.target instanceof HTMLSelectElement)) {
      return;
    }

    const currentNumberOfNonBlankOverlays = overlays.value.filter(overlay => overlay?.overlayName && overlay.overlayName !== NONE);

    if (
      !isIndicatorOverlayMenu.value &&
      !isLoggedIn.value &&
      currentNumberOfNonBlankOverlays.length >= MAXIMUM_NUMBER_OF_OVERLAYS_FOR_FREE_USERS &&
      parentOverlayName.value === NONE
    ) {
      syncOverlayName();
      launchCreateAccountModal();
      return;
    }

    emit('update:overlayName', e.target.value);
    emit('overlayMenuChanged', id.value, e.target.value);
  }

  function syncOverlayName() {
    overlayName.value = parentOverlayName.value;
  }

  watch(parentOverlayName, syncOverlayName);
</script>

<style scoped>
  .hidden {
    visibility: hidden;
  }
</style>
